import { type BuilderContent, Content } from "@builder.io/sdk-react";

import { customComponents } from "~/hec/builder";
import { GuardedBuilderLink } from "~/hec/builder/guarded-builder-link";
import { useBuilder } from "~/hec/hooks/useBuilder";

import { PrefetchBuilderAnchors } from "./prefetch-builder-anchors";

/**
 * A Builder `<Content>` slot whose internal CMS anchors are SPA-delegated and
 * intent-prefetched (SFCC-11297). Use this for every standalone Builder
 * content slot so author-placed links navigate client-side instead of doing a
 * full-document reload — callers don't have to remember the `PrefetchBuilderAnchors`
 * wrap or thread `apiKey` / `customComponents` themselves.
 *
 * No `enrich` prop (COM-586) — see docs/working-conventions.md §10.
 *
 * (Not for raw embedded HTML or mixed branches — wrap those in
 * `<PrefetchBuilderAnchors>` directly.)
 */
export function PrefetchedBuilderContent({
  model,
  content,
  canTrack,
}: {
  model: string;
  content: BuilderContent | null;
  /**
   * Pass `false` for a secondary region of an entry another slot already
   * renders (see `scopeRegionId`) — required there: it keeps Builder A/B
   * variants (whose blocks are the MAIN canvas) out of the region and logs one
   * impression per entry. Also disables click tracking. Omitted = SDK default.
   */
  canTrack?: boolean;
}) {
  const builder = useBuilder();
  return (
    <PrefetchBuilderAnchors>
      <Content
        model={model}
        content={content}
        canTrack={canTrack}
        apiKey={builder.config.api_key}
        customComponents={customComponents}
        linkComponent={GuardedBuilderLink}
      />
    </PrefetchBuilderAnchors>
  );
}
